import React from 'react';
import useThemeCanvas from '../useThemeCanvas';

// Golden Hour — a real sunset photograph brought to life: a slow Ken Burns
// drift, soft god rays from the sun, drifting dust motes in the light, and a
// gentle exposure breath. Falls back to a warm gradient if the photo is absent.

const BG_SRC = '/textures/synthwave_bg.jpg';
// Sun position in the photo (fraction of width/height) — tune if you swap the image.
const SUN = { x: 0.23, y: 0.37 };
const MOTE_COUNT = 34;

function coverDims(img, w, h, zoom) {
  const ir = img.width / img.height;
  const cr = w / h;
  let dw, dh;
  if (cr > ir) { dw = w; dh = w / ir; } else { dh = h; dw = h * ir; }
  return { dw: dw * zoom, dh: dh * zoom };
}

export default function SynthwaveTheme({ palette, colorMode, dark }) {
  const canvasRef = useThemeCanvas({
    init: (ctx, w, h, state) => {
      state.motes = Array.from({ length: MOTE_COUNT }, () => ({
        x: Math.random() * w,
        y: Math.random() * h,
        r: 0.8 + Math.random() * 2.2,
        sp: 0.08 + Math.random() * 0.22,
        ph: Math.random() * Math.PI * 2,
      }));
      if (state.bgImg === undefined) {
        state.bgImg = null;
        const img = new Image();
        img.onload = () => { state.bgImg = img; };
        img.onerror = () => { state.bgImg = false; };
        img.src = BG_SRC;
      }
    },
    draw: (ctx, w, h, state, t) => {
      const sunX = w * SUN.x;
      const sunY = h * SUN.y;

      if (state.bgImg && state.bgImg !== false) {
        // Living photo: slow Ken Burns zoom + drift.
        const zoom = 1.06 + 0.04 * Math.sin(t * 0.035);
        const panX = Math.sin(t * 0.03) * w * 0.015;
        const panY = Math.cos(t * 0.024) * h * 0.012;
        const { dw, dh } = coverDims(state.bgImg, w, h, zoom);
        ctx.drawImage(state.bgImg, (w - dw) / 2 + panX, (h - dh) / 2 + panY, dw, dh);
      } else {
        // Fallback warm gradient.
        const g = ctx.createLinearGradient(0, 0, 0, h);
        g.addColorStop(0, '#243b6b');
        g.addColorStop(0.5, '#c2724e');
        g.addColorStop(1, '#f0b066');
        ctx.fillStyle = g;
        ctx.fillRect(0, 0, w, h);
      }

      // Gentle exposure breath — a soft warm wash that pulses slightly.
      const breath = 0.5 + 0.5 * Math.sin(t * 0.25);
      ctx.save();
      ctx.globalCompositeOperation = 'screen';

      // God rays radiating from the sun, slowly rotating.
      ctx.translate(sunX, sunY);
      const rays = 9;
      const len = Math.max(w, h) * 1.2;
      for (let i = 0; i < rays; i++) {
        const ang = (i / rays) * Math.PI * 2 + t * 0.02;
        const spread = 0.06 + 0.02 * Math.sin(t * 0.5 + i);
        ctx.save();
        ctx.rotate(ang);
        const rg = ctx.createLinearGradient(0, 0, len, 0);
        rg.addColorStop(0, `rgba(255,220,150,${0.05 + 0.03 * breath})`);
        rg.addColorStop(1, 'rgba(255,220,150,0)');
        ctx.fillStyle = rg;
        ctx.beginPath();
        ctx.moveTo(0, 0);
        ctx.lineTo(len, -len * spread);
        ctx.lineTo(len, len * spread);
        ctx.closePath();
        ctx.fill();
        ctx.restore();
      }
      ctx.restore();

      // Soft sun bloom that breathes.
      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      const bloomR = Math.min(w, h) * (0.28 + 0.03 * breath);
      const bloom = ctx.createRadialGradient(sunX, sunY, 0, sunX, sunY, bloomR);
      bloom.addColorStop(0, `rgba(255,236,190,${0.32 + 0.1 * breath})`);
      bloom.addColorStop(0.5, 'rgba(255,200,120,0.12)');
      bloom.addColorStop(1, 'rgba(255,200,120,0)');
      ctx.fillStyle = bloom;
      ctx.fillRect(0, 0, w, h);
      ctx.restore();

      // Dust motes drifting toward the light.
      ctx.save();
      ctx.globalCompositeOperation = 'screen';
      for (const m of state.motes) {
        // drift gently toward the sun + float
        m.x += (sunX - m.x) * 0.0004 + Math.sin(t * 0.3 + m.ph) * 0.2;
        m.y += -m.sp + Math.cos(t * 0.25 + m.ph) * 0.15;
        if (m.y < -8 || m.x < -8 || m.x > w + 8) { m.x = Math.random() * w; m.y = h + 8; }
        const tw = 0.3 + 0.7 * Math.abs(Math.sin(t * 0.8 + m.ph));
        ctx.fillStyle = `rgba(255,228,170,${0.22 * tw})`;
        ctx.beginPath();
        ctx.arc(m.x, m.y, m.r, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.restore();

      // Warm cinematic vignette.
      const vig = ctx.createRadialGradient(w / 2, h / 2, Math.min(w, h) * 0.42, w / 2, h / 2, Math.max(w, h) * 0.72);
      vig.addColorStop(0, 'rgba(0,0,0,0)');
      vig.addColorStop(1, dark ? 'rgba(10,6,2,0.5)' : 'rgba(20,12,4,0.28)');
      ctx.fillStyle = vig;
      ctx.fillRect(0, 0, w, h);
    },
  });
  return <canvas ref={canvasRef} className="absolute inset-0" />;
}
